/**
 * useBriefWorkspaceChat — admin-side wrapper around @neurograph/brief-ui's
 * useBriefChat hook.
 *
 * Phase 0 strategy: transparently mint a long-lived internal `edit` token
 * for the admin user and use it as the bearer credential against the
 * existing public token-gated endpoints (`/api/v1/public/brief/{token}/...`).
 * No iframe — chat is rendered natively inside the workspace using the
 * shared @brief-ui components.
 *
 * Phase 1+ may swap this for direct admin-JWT chat endpoints; the consumer
 * surface stays the same.
 */

import { useEffect, useRef, useState } from 'react';
import { useBriefChat, type UseBriefChatReturn } from '@neurograph/brief-ui';
import { briefShareApi, getApiBase, type BriefShareTokenResponse } from '@/services/api';
import { workspaceBriefApi } from './api';

const TOKEN_KEY = (briefId: string) => `brief-workspace:${briefId}`;

export interface UseBriefWorkspaceChatReturn extends UseBriefChatReturn {
  token: string | null;
  tokenInfo: BriefShareTokenResponse | null;
  tokenLoading: boolean;
  tokenError: string | null;
  shareUrl: string | null;
}

interface Options {
  onBriefPatched?: (section: string, after: Record<string, unknown> | null) => void;
}

export function useBriefWorkspaceChat(
  briefId: string,
  clientUid: string,
  opts: Options = {},
): UseBriefWorkspaceChatReturn {
  const [tokenInfo, setTokenInfo] = useState<BriefShareTokenResponse | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const mintedRef = useRef(false);

  useEffect(() => {
    mintedRef.current = false;
    setTokenInfo(null);
    setTokenError(null);
  }, [briefId]);

  useEffect(() => {
    if (!briefId || mintedRef.current) return;
    mintedRef.current = true;

    const cached = sessionStorage.getItem(TOKEN_KEY(briefId));
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as BriefShareTokenResponse;
        // Only use cached token if it expires more than 60 minutes from now.
        // This gives plenty of runway and avoids stale-token HTML responses.
        const expiresAt = new Date(parsed.expires_at).getTime();
        if (expiresAt > Date.now() + 60 * 60 * 1000) {
          setTokenInfo(parsed);
          return;
        }
      } catch {
        /* fall through and remint */
      }
      sessionStorage.removeItem(TOKEN_KEY(briefId));
    }

    const doMint = () => {
      setTokenLoading(true);
      briefShareApi
        .mint(briefId, {
          permissions: 'edit',
          expires_in_days: 30,
          metadata: { source: 'admin_workspace', minted_for: clientUid },
        })
        .then((res) => {
          setTokenInfo(res);
          try {
            sessionStorage.setItem(TOKEN_KEY(briefId), JSON.stringify(res));
          } catch {
            /* quota errors are non-fatal */
          }
        })
        .catch((e) => setTokenError((e as Error).message || 'Failed to open brief workspace'))
        .finally(() => setTokenLoading(false));
    };
    doMint();
  }, [briefId, clientUid]);

  const chat = useBriefChat(tokenInfo?.token ?? '', {
    onBriefPatched: opts.onBriefPatched,
    initialEdits: tokenInfo?.edits_remaining ?? null,
    initialGenerations: tokenInfo?.generations_remaining ?? null,
    apiClient: workspaceBriefApi,
    apiBase: getApiBase(),
  });

  return {
    ...chat,
    token: tokenInfo?.token ?? null,
    tokenInfo,
    tokenLoading,
    tokenError,
    shareUrl: tokenInfo?.share_url ?? null,
  };
}

// Re-export the apiClient so the SuggestionRail / quickPatch consumers can
// use the same instance without importing from `./api` directly.
export { workspaceBriefApi };
